import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import type {
  StudyGroup,
  ViewportConfiguration,
  ViewportsConfigurations,
  DehydratedViewportsConfigurations,
} from './types';
import { DEFAULT_LAYOUT } from '../Viewer/StudyLoader/viewerLoaderState';
import { viewportIdToConfig } from '../Viewer/viewerUtils';
import { mergeViewportsConfigurations, filterViewportsConfigurations } from './manipulators';
import analytics from 'modules/analytics';
import { CUSTOM_TIMINGS } from 'modules/analytics/constants';

/**
 * Checks if the viewport will be visible in the viewer layout.
 */
function isViewportInLayout(windows: ReadonlyArray<string>, layouts: Layouts) {
  return ([viewportId, viewportConfiguration]: [any, any]) => {
    const { windowId, colIndex, rowIndex } = viewportIdToConfig(viewportId);
    return (
      windows.includes(windowId) &&
      (layouts?.[windowId] ?? DEFAULT_LAYOUT)[0] > rowIndex &&
      (layouts?.[windowId] ?? DEFAULT_LAYOUT)[1] > colIndex
    );
  };
}

type GenerateViewportsConfigurationsArgs = {
  defaults: Nullable<Partial<ViewportConfiguration>>;
  groupedStudies: StudyGroup[];
  layouts: Layouts;
  windows: ReadonlyArray<string>;
  viewportsConfigurations: Nullable<ViewportsConfigurations | DehydratedViewportsConfigurations>;
  hangingProtocolViewportsConfiguration: Nullable<ViewportsConfigurations>;
  isPreviewingHangingProtocol: boolean;
};

export function generateViewportsConfigurations({
  groupedStudies,
  layouts,
  windows,
  viewportsConfigurations,
  hangingProtocolViewportsConfiguration,
  isPreviewingHangingProtocol,
}: GenerateViewportsConfigurationsArgs): ViewportsConfigurations {
  // The base case for viewports is the currently visible portion of the hanging protocol
  const baseViewportsConfigurations = filterViewportsConfigurations(
    hangingProtocolViewportsConfiguration ?? {},
    isViewportInLayout(windows, layouts)
  );

  const availableSeries = groupedStudies.flatMap((studyGroup) =>
    studyGroup.flatMap((study) => study.seriesList.flatMap((series) => series.smid))
  );

  // Filter the configuration to only include viewports that have a series
  // currently loaded in the viewer and that where dropped by a user.
  const onlyCurrentSeriesViewportsConfigurations =
    viewportsConfigurations != null
      ? filterViewportsConfigurations(
          viewportsConfigurations,
          ([viewportId, viewportConfiguration]: [any, any]) =>
            availableSeries.includes(viewportConfiguration?.series?.smid) ||
            viewportConfiguration?.wasDropped === true || // May force an empty viewport when setting next batch
            viewportConfiguration == null // Can happen if the viewport was cleared
        )
      : null;

  // Remove any viewports pulled from the recoil state that don't match active viewer windows and layouts
  const onlyActiveWindowsViewportsConfigurations =
    onlyCurrentSeriesViewportsConfigurations != null
      ? filterViewportsConfigurations(
          onlyCurrentSeriesViewportsConfigurations,
          isViewportInLayout(windows, layouts)
        )
      : null;

  // Drop from the atom-persisted configuration anything that is not directly
  // matching the user-intent or the fallback HP.
  const cleanedFromNotDroppedViewportsConfigurations =
    onlyActiveWindowsViewportsConfigurations != null
      ? filterViewportsConfigurations(
          onlyActiveWindowsViewportsConfigurations,
          ([viewportId, viewportConfiguration]: [any, any]) => {
            // If the viewport configuration is null we can KEEP it
            if (viewportConfiguration == null) {
              return true;
            }
            // If it was dropped by the user we want to KEEP it
            // If it was dragged by the user we want to UPDATE it
            if (
              viewportConfiguration.wasDropped === true ||
              viewportConfiguration.wasDragged === true
            ) {
              return true;
            }

            // If it was not dropped by the user but it's displaying the same
            // series then we want to KEEP it
            const seriesSmid = viewportConfiguration.series?.smid;
            const incomingViewportConfiguration = baseViewportsConfigurations[viewportId];
            if (incomingViewportConfiguration?.series?.smid === seriesSmid) {
              return true;
            }

            // Otherwise we want to DROP it
            return false;
          }
        )
      : null;

  const mergedViewportsConfigurations = mergeViewportsConfigurations(
    baseViewportsConfigurations,
    // Get the viewport configurations defined by our user
    !isPreviewingHangingProtocol ? cleanedFromNotDroppedViewportsConfigurations : null
  );

  const processedViewportsConfigurations = Object.fromEntries(
    Object.entries(mergedViewportsConfigurations).map(([key, config]) => [
      key,
      config != null && config.study == null ? null : config,
    ])
  );

  if (Object.keys(processedViewportsConfigurations).length > 0) {
    analytics.timing(CUSTOM_TIMINGS.viewer.hangingProtocolLoadComplete);
  }

  return processedViewportsConfigurations;
}
